import { ArrowDownIcon } from '@/components/icons'
import useTranslation from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import {
  Box,
  FormControl,
  Grid,
  List,
  MenuItem,
  Select,
  SelectChangeEvent,
  selectClasses,
  Typography,
} from '@mui/material'
import { UsePaginationItem } from '@mui/material/usePagination/usePagination'
import { Stack } from '@mui/system'
import { useSelector } from 'react-redux'

interface TableFooterProps {
  items: UsePaginationItem[]
  totalPage?: number
  totalCount?: number
  page?: number
  rowsPerPage: number
  handleChangeRowsPerPage: (event: SelectChangeEvent) => void
  noPadding?: boolean
}

const TableFooter = ({
  totalPage,
  totalCount,
  rowsPerPage,
  page,
  handleChangeRowsPerPage,
  items,
  noPadding = false,
}: TableFooterProps) => {
  const { t } = useTranslation()
  const themeMode = useSelector(selectTheme)

  // Dynamic validRowsPerPage logic
  const validRowsPerPage = [10, 25, 50, 100].includes(rowsPerPage)
                            ? rowsPerPage
                            : [10, 25, 50, 100].find(item => rowsPerPage <= item) || 100;
  if (!totalCount) return null

  // Pagination controls (Select + buttons)
  const paginationControls = (
    <Grid alignItems="center" sx={{ display: 'flex' }} gap="6px">
      <FormControl>
        {validRowsPerPage > 0 && (
          <Select
            value={validRowsPerPage.toString()}
            onChange={handleChangeRowsPerPage}
            IconComponent={ArrowDownIcon}
            inputProps={{ 'aria-label': 'Without label' }}
            sx={{
              height: '40px',
              [`& .${selectClasses.select}`]: {
                padding: '10px 11px 8px',
                fontWeight: 400,
                fontSize: 12,
                paddingRight: '64px',
                lineHeight: '100%',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiSelect-icon': {
                color: 'text.main',
              },
            }}
            MenuProps={{
              sx: {
                '&& .MuiList-root': {
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  paddingRight: '0px',
                },
              },
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
              },
              transformOrigin: {
                vertical: 'bottom',
                horizontal: 'center',
              },
            }}
          >
            <MenuItem
              value={10}
              sx={{
                fontWeight: 400,
                fontSize: 12,
                lineHeight: '100%',
              }}
            >
              <Typography
                sx={{
                  width: '99px',
                }}
              >
                {t('paginationPerPage', { count: 10 })}
              </Typography>
            </MenuItem>

            <MenuItem
              value={25}
              sx={{
                fontWeight: 400,
                fontSize: 12,
                lineHeight: '100%',
              }}
            >
              <Typography
                sx={{
                  width: '99px',
                }}
              >
                {t('paginationPerPage', { count: 25 })}
              </Typography>
            </MenuItem>
            <MenuItem
              value={50}
              sx={{
                fontWeight: 400,
                fontSize: 12,
                lineHeight: '100%',
              }}
            >
              <Typography
                sx={{
                  width: '99px',
                }}
              >
                {t('paginationPerPage', { count: 50 })}
              </Typography>
            </MenuItem>
            <MenuItem
              value={100}
              sx={{
                fontWeight: 400,
                fontSize: 12,
                lineHeight: '100%',
              }}
            >
              <Typography
                sx={{
                  width: '99px',
                }}
              >
                {t('paginationPerPage', { count: 100 })}
              </Typography>
            </MenuItem>
          </Select>
        )}
      </FormControl>
      <List>
        <Stack direction="row">
          {items.map(
            ({ page, type, selected, ...item }: any, index: number) => {
              let children = null

              if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                children = (
                  <button
                    type="button"
                    style={{
                      height: '38px',
                      width: '30px',
                      backgroundColor: 'white',
                      borderRight: '1px solid #D9D9D9',
                      borderTop: '1px solid #D9D9D9',
                      borderBottom: '1px solid #D9D9D9',
                      borderLeft: '0px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                    {...item}
                  >
                    ...
                  </button>
                )
              } else if (type === 'page') {
                children = (
                  <button
                    type="button"
                    style={{
                      fontWeight: selected ? 'bold' : undefined,
                      color: selected
                        ? themeMode === mode_type.dark
                          ? '#004744'
                          : 'white'
                        : themeMode === mode_type.dark
                        ? 'white'
                        : '#000000',
                      height: '38px',
                      width: '30px',
                      backgroundColor: selected
                        ? themeMode === mode_type.dark
                          ? ' #ebf1f0'
                          : '#004744'
                        : themeMode === mode_type.dark
                        ? 'transparent'
                        : '#FFF',
                      border:
                        themeMode === mode_type.dark
                          ? '1px solid #283D43'
                          : '1px solid #D9D9D9',
                      borderLeft: '0px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                    {...item}
                  >
                    {page}
                  </button>
                )
              } else {
                if (index === items.length - 1) {
                  children = (
                    <button
                      type="button"
                      style={{
                        height: '38px',
                        width: '63px',
                        border:
                          themeMode === mode_type.dark
                            ? '1px solid #283D43'
                            : '1px solid #D9D9D9',
                        color:
                          themeMode === mode_type.dark ? 'white' : '#000',
                        backgroundColor:
                          themeMode === mode_type.dark
                            ? 'transparent'
                            : 'white',
                        textTransform: 'capitalize',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                      {...item}
                    >
                      {t(
                        `navigationBreadcrumbs${
                          type.charAt(0).toUpperCase() + type.slice(1)
                        }`,
                      )}
                    </button>
                  )
                } else {
                  children = (
                    <button
                      type="button"
                      style={{
                        height: '38px',
                        width: '89px',
                        border:
                          themeMode === mode_type.dark
                            ? '1px solid #283D43'
                            : '1px solid #D9D9D9',
                        color:
                          themeMode === mode_type.dark ? 'white' : '#000',
                        backgroundColor:
                          themeMode === mode_type.dark
                            ? 'transparent'
                            : 'white',
                        textTransform: 'capitalize',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                      {...item}
                    >
                      {t(
                        `navigationBreadcrumbs${
                          type.charAt(0).toUpperCase() + type.slice(1)
                        }`,
                      )}
                    </button>
                  )
                }
              }

              return <li key={index}>{children}</li>
            },
          )}
        </Stack>
      </List>
    </Grid>
  )

  // If noPadding, return only the pagination controls (for inline usage)
  if (noPadding) {
    return paginationControls
  }

  // Full layout with wrapper
  return (
    <Stack
      sx={{
        left: '0',
        width: '100%',
        padding: '22px 2px',
      }}
    >
      <Grid container alignItems="center" justifyContent="space-between">
        {!!totalCount && page ? (
          <Typography color="#999" lineHeight={1}>
            {t('paginationShowing', {
              from: validRowsPerPage * (page - 1) + 1,
              to: Math.min(validRowsPerPage * page, totalCount),
              total: totalCount,
            })}
          </Typography>
        ) : (
          <Box />
        )}
        {paginationControls}
      </Grid>
    </Stack>
  )
}

export default TableFooter
